// Phase 129 — Top-level demo section.
//
// Orchestrates the SSE hook, renders the short-code card OR the chat panel
// based on whether the user has bound (sent the code from their phone yet),
// and overlays the takeover modal when another tab steals the bind.
//
// PostHog events emitted as the demo progresses:
//   - demo_short_code_shown   — once when code arrives via event: ready
//   - demo_first_inbound      — once when the first phone message comes back
//   - demo_handoff_clicked    — fired by demo-chat-panel.tsx
import { useEffect, useRef, useState } from 'react';

import { useDemoStream } from './use-demo-stream';
import { DemoCodeCard } from './demo-code-card';
import { DemoChatPanel } from './demo-chat-panel';
import { DemoTakeoverOverlay } from './demo-takeover-overlay';
import { DemoPhoneClaimedOverlay } from './demo-phone-claimed-overlay';
import { emit } from '../../lib/analytics';
import type { Channel } from '../../lib/channel-copy';

export function DemoSection({ channel: pageChannel = 'both' }: { channel?: Channel } = {}) {
  const {
    code,
    demoSessionId,
    messages,
    bound,
    takenOver,
    phoneClaimed,
    phoneClaimedMessage,
    boundPhone,
    boundSender,
    sendReply,
    reclaim,
    resetBind,
  } = useDemoStream();
  const sandboxPhone = import.meta.env.VITE_SANDBOX_PHONE_NUMBER ?? '972557046276';
  // Channel tabs are a UX hint only. The backend stays channel-agnostic:
  // whichever provider sends the code first binds the session. Switching tabs
  // just swaps the on-screen instructions (wa.me vs ig.me).
  //
  // AIT-268: on a per-channel landing page the demo IS that channel — the tabs
  // are not rendered and the instructions are fixed. Only the homepage
  // ('both') offers the switch.
  const singleChannel = pageChannel === 'both' ? null : pageChannel;
  const [channel, setChannel] = useState<'whatsapp' | 'instagram'>(
    singleChannel ?? 'whatsapp',
  );

  const codeShownEmittedRef = useRef(false);
  useEffect(() => {
    if (code && demoSessionId && !codeShownEmittedRef.current) {
      emit('demo_short_code_shown', { demo_session_id: demoSessionId });
      codeShownEmittedRef.current = true;
    }
  }, [code, demoSessionId]);

  const inboundEmittedRef = useRef(false);
  useEffect(() => {
    if (bound && demoSessionId && !inboundEmittedRef.current) {
      emit('demo_first_inbound', { demo_session_id: demoSessionId, latency_ms: 0 });
      inboundEmittedRef.current = true;
    }
  }, [bound, demoSessionId]);

  // takeOver / reclaim aren't yet in the EventRegistry — keeping the funnel
  // surface stable. If/when we add `demo_taken_over` + `demo_reclaim_clicked`
  // we'll register them in @hookmyapp/observability + the CLI mirror at the
  // same time (the manifest-drift tests guard set-equality across both).
  const handleReclaim = async (): Promise<void> => {
    await reclaim();
  };

  return (
    <section
      id="demo"
      data-testid="demo-section"
      className="relative overflow-hidden bg-gradient-to-b from-black via-[#0E1012] to-black px-6 py-16 md:py-24"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-1/3 h-[600px] w-[1000px] -translate-x-1/2 rounded-full bg-[#0064E0]/10 blur-[180px]"
      />
      <div className="relative mx-auto max-w-4xl">
        <h2 className="text-4xl md:text-5xl font-medium tracking-tight leading-[1.17] mb-12 text-center text-white" style={{ letterSpacing: '-0.015em' }}>
          Try it now
        </h2>
        {!(bound && demoSessionId) && !singleChannel ? (
          <div role="tablist" aria-label="Demo channel" className="mb-8 flex justify-center">
            <div className="inline-flex rounded-full border border-white/10 bg-white/[0.04] p-1">
              {(['whatsapp', 'instagram'] as const).map((c) => (
                <button
                  key={c}
                  type="button"
                  role="tab"
                  aria-selected={channel === c}
                  onClick={() => setChannel(c)}
                  className={`rounded-full px-6 py-2 text-sm font-medium transition ${
                    channel === c
                      ? 'bg-[#0064E0] text-white'
                      : 'text-white/60 hover:text-white'
                  }`}
                >
                  {c === 'whatsapp' ? 'WhatsApp' : 'Instagram'}
                </button>
              ))}
            </div>
          </div>
        ) : null}
        <div className="relative">
          {bound && demoSessionId ? (
            <DemoChatPanel
              demoSessionId={demoSessionId}
              messages={messages}
              onSend={sendReply}
              dimmed={takenOver || phoneClaimed}
              boundPhone={boundPhone}
              boundSender={boundSender}
              onChangeNumber={resetBind}
            />
          ) : code ? (
            <DemoCodeCard
              code={code}
              sandboxPhone={sandboxPhone}
              demoSessionId={demoSessionId}
              channel={channel}
            />
          ) : (
            <div
              role="status"
              aria-live="polite"
              className="rounded-[24px] bg-[#181A1B] border border-white/10 p-6 md:p-8 shadow-[0_12px_28px_rgba(0,0,0,0.2),0_2px_4px_rgba(0,0,0,0.1)] flex flex-col h-[422px]"
            >
              <div className="flex items-center justify-between gap-3 border-b border-white/10 pb-3 mb-3">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="size-1.5 rounded-full bg-white/20" aria-hidden="true" />
                  <span className="h-3 w-24 rounded bg-white/10 animate-pulse" aria-hidden="true" />
                </div>
                <span className="h-6 w-20 rounded-full bg-white/[0.04] border border-white/10" aria-hidden="true" />
              </div>
              <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-3 text-center">
                <svg
                  className="animate-spin text-[#0064E0]"
                  xmlns="http://www.w3.org/2000/svg"
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
                <span className="text-sm text-white/60">Connecting...</span>
              </div>
            </div>
          )}
          {/* Phase 130 (Rule 1 + Codes Unification, Task 9). Terminal state —
              the phone tied to this demo's sandboxSessionPublicId has been
              adopted by a signed-up account. Takes precedence over the
              takeover overlay (the SSE handler also clears takenOver, so in
              practice they don't co-render, but the explicit guard makes the
              intent visible). */}
          {phoneClaimed ? (
            <DemoPhoneClaimedOverlay message={phoneClaimedMessage} />
          ) : takenOver ? (
            <DemoTakeoverOverlay onReclaim={handleReclaim} />
          ) : null}
        </div>
      </div>
    </section>
  );
}
